import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuthStore } from "../../Store/auth";
import {
  FiDownload,
  FiLink,
  FiShare2,
  FiRefreshCw,
  FiCreditCard,
  FiCheck,
  FiAlertTriangle,
  FiCopy,
  FiExternalLink,
  FiChevronRight,
  FiInfo,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { API_ORIGIN } from "../../config/api";

// === CONFIG ===
const API = API_ORIGIN;

// === small utils ===
const money = (n) => (n == null || Number.isNaN(Number(n)) ? 0 : Number(n));

const fmtMoney = (x) =>
  x == null || Number.isNaN(Number(x))
    ? "—"
    : `AED ${Number(x).toLocaleString("en-AE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const net80 = (amount) => Math.max(0, Math.round(amount * 80) / 100);
const fee20 = (amount) => Math.max(0, Math.round(amount * 20) / 100);

function Badge({ status }) {
  const s = String(status || "").toLowerCase();
  const cls =
    s === "succeeded"
      ? "bg-emerald-100 text-emerald-800"
      : s === "refunded"
      ? "bg-rose-100 text-rose-800"
      : s === "pending"
      ? "bg-amber-100 text-amber-800"
      : "bg-gray-100 text-gray-800";
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs capitalize ${cls}`}>
      {s || "—"}
    </span>
  );
}
const Th = ({ children, className }) => (
  <th className={`py-2 px-3 ${className || ""}`}>{children}</th>
);
const Td = ({ children, className, colSpan }) => (
  <td className={`py-2 px-3 ${className || ""}`} colSpan={colSpan}>
    {children}
  </td>
);

const Card = ({ label, value, hint }) => (
  <div className="bg-white/70 backdrop-blur rounded-2xl border p-4 shadow-sm min-w-[160px]">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">
      {label}
    </div>
    <div className="text-xl font-semibold mt-1">{value ?? "—"}</div>
    {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
  </div>
);

// === avoid Recharts -1 width/height ===
function useContainerSize() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box?.width) setW(box.width);
    });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, w];
}

// ---- helper: friendly status text for payouts ----
function getPayoutUiState(connect) {
  if (connect.loading) {
    return {
      tone: "info",
      title: "Checking payouts status…",
      text: "Please wait a moment.",
    };
  }

  if (!connect.connected) {
    return {
      tone: "warn",
      title: "Not connected yet",
      text:
        "Connect Stripe to receive Thanks Gifts. PTfinderNow never sees your bank details — Stripe handles verification and payouts securely.",
    };
  }

  const req = Array.isArray(connect.requirements) ? connect.requirements : [];
  const past = Array.isArray(connect.pastDue) ? connect.pastDue : [];
  const disabled = String(connect.disabledReason || "").toLowerCase();

  const needsInfo = req.length > 0 || past.length > 0;
  const notEnabled = !connect.chargesEnabled || !connect.payoutsEnabled;

  if (disabled && disabled !== "other") {
    return {
      tone: "warn",
      title: "Action required",
      text:
        "Stripe is asking for additional steps to enable payouts. Click “Finish Stripe setup” and complete the requested information.",
    };
  }

  if (needsInfo || notEnabled) {
    return {
      tone: "warn",
      title: "Finish Stripe setup",
      text:
        "You’ve created a Stripe account, but payouts are not enabled yet. Click “Finish Stripe setup”. After you finish, come back here and press Refresh.",
    };
  }

  return {
    tone: "ok",
    title: "Ready to receive gifts ✅",
    text:
      "Your payouts are enabled. You can receive Thanks Gifts and view your payouts in Stripe.",
  };
}

export default function DashboardGifts() {
  // auth
  const s = useAuthStore.getState?.() || {};
  const h = useAuthStore();
  const coachId = s.coachId || h.coachId || "";
  const token = s.token || h.token || "";
  const auth = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    [token]
  );

  const [loading, setLoading] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [stats, setStats] = useState(null);
  const [connect, setConnect] = useState({
    loading: true,
    connected: false,
    chargesEnabled: false,
    payoutsEnabled: false,
    accountId: undefined,
    requirements: [],
    pastDue: [],
    disabledReason: undefined,
    canLoginLink: false,
  });

  const [sheetOpen, setSheetOpen] = useState(false);
  const [chartRef, width] = useContainerSize();
  const giftLink = `${window.location.origin}/coach/${coachId}?tab=thanks`;

  // ---- toasts (tiny) ----
  const [toasts, setToasts] = useState([]);
  const toast = (text) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 1600);
  };

  // helpers
  const copy = async (t) => {
    try {
      await navigator.clipboard.writeText(t);
      toast("Copied");
    } catch {}
  };
  const exportCsv = async () => {
    try {
      const response = await axios.get(`${API}/api/Gifts/coach/${coachId}/export`, { ...auth, responseType: "text" });
      if (typeof response.data !== "string" || !response.data.startsWith("Id,CoachId,")) throw new Error("Invalid export");
      const url = URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url; link.download = "ptfindernow-gifts.csv";
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast("Could not export gifts. Please sign in again or retry."); }
  };

  // ---- API calls ----
  const fetchConnectStatus = async () => {
    if (!coachId) return;
    try {
      setConnect((s) => ({ ...s, loading: true }));
      const { data } = await axios.get(
        `${API}/api/Billing/connect/status/${coachId}`,
        auth
      );
      setConnect({
        loading: false,
        connected: !!data?.connected,
        chargesEnabled: !!data?.chargesEnabled,
        payoutsEnabled: !!data?.payoutsEnabled,
        accountId: data?.accountId,
        requirements: Array.isArray(data?.requirements) ? data.requirements : [],
        pastDue: Array.isArray(data?.pastDue) ? data.pastDue : [],
        disabledReason: data?.disabledReason,
        canLoginLink: !!data?.connected,
      });
    } catch {
      setConnect({
        loading: false,
        connected: false,
        chargesEnabled: false,
        payoutsEnabled: false,
        accountId: undefined,
        requirements: [],
        pastDue: [],
        disabledReason: undefined,
        canLoginLink: false,
      });
    }
  };

  const fetchAll = async () => {
    if (!coachId || !token) return;
    setLoading(true);
    try {
      // stats
      let statsData = {
        totalAllTime: 0,
        totalThisMonth: 0,
        total7d: 0,
        avgGift: 0,
        topGift: 0,
        series: [],
      };
      try {
        const s = await axios.get(
          `${API}/api/Gifts/coach/${coachId}/stats?range=30d`,
          auth
        );
        statsData = s.data || statsData;
      } catch {}
      setStats(statsData);

      // gifts
      let list = [];
      try {
        const g = await axios.get(`${API}/api/Gifts/coach/${coachId}`, auth);
        list = Array.isArray(g.data) ? g.data : g.data?.$values ?? [];
      } catch {}
      setGifts(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectStatus();
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId]);

  const setupPayouts = async () => {
    try {
      await axios.post(
        `${API}/api/Billing/connect/account`,
        { coachId: String(coachId) },
        auth
      );
      const r2 = await axios.post(
        `${API}/api/Billing/connect/account-link`,
        { coachId: String(coachId) },
        auth
      );
      window.location.href = r2.data.url;
    } catch (e) {
      console.warn("setupPayouts error:", e?.response?.data || e);
      alert(e?.response?.data?.message || "Setup failed");
    }
  };

  const refreshConnect = async () => {
    await fetchConnectStatus();
    if (!connect.connected) toast("Not connected yet");
  };

  // open Stripe Express dashboard
  const openStripePayouts = async () => {
    try {
      const { data } = await axios.post(
        `${API}/api/Billing/connect/login-link`,
        { coachId: String(coachId) },
        auth
      );
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      alert("Could not open Stripe payouts");
    }
  };

  // derived KPIs (net/fee)
  const grossAll = money(stats?.totalAllTime);
  const netAll = net80(grossAll);
  const feeAll = fee20(grossAll);

  // consolidated onboarding state
  const needsOnboarding =
    !connect.payoutsEnabled ||
    !connect.chargesEnabled ||
    (connect.requirements?.length || 0) > 0 ||
    (connect.pastDue?.length || 0) > 0 ||
    [
      "under_review",
      "requirements_pending",
      "rejected.other",
      "rejected.fraud",
    ].includes(String(connect.disabledReason || "").toLowerCase());

  const payoutUi = getPayoutUiState(connect);

  const payoutBoxCls =
    payoutUi.tone === "ok"
      ? "bg-emerald-50 border-emerald-200 text-emerald-900"
      : payoutUi.tone === "warn"
      ? "bg-amber-50 border-amber-200 text-amber-900"
      : "bg-sky-50 border-sky-200 text-sky-900";

  // === UI ===
  return (
    <div className="relative">
      {/* gradient hero */}
      <div className="sticky top-0 z-30 -mx-4 px-4 pt-3 pb-4 bg-gradient-to-b from-sky-50 to-white border-b">
        <div className="flex items-center gap-3">
          <h1 className="text-xl md:text-2xl font-semibold">Gifts</h1>
          <span className="text-xs text-gray-600 ml-1 flex items-center gap-1">
            <FiInfo />
            Coaches receive <b className="ml-1">80%</b> of each gift. Platform
            fee is <b className="ml-1">20%</b>.
          </span>
          <button
            onClick={fetchAll}
            className="ml-auto px-3 py-1.5 rounded-lg bg-gray-900 text-white flex items-center gap-2 active:scale-95"
            aria-label="Refresh"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* horizontal KPIs */}
        <div className="mt-3 overflow-x-auto no-scrollbar">
          <div className="flex gap-3 w-max">
            <Card label="Total (gross)" value={fmtMoney(grossAll)} />
            <Card
              label="Your net (80%)"
              value={fmtMoney(netAll)}
              hint="What you receive"
            />
            <Card label="Platform fees (20%)" value={fmtMoney(feeAll)} />
            <Card
              label="This month (gross)"
              value={fmtMoney(stats?.totalThisMonth)}
            />
            <Card label="Last 7 days (gross)" value={fmtMoney(stats?.total7d)} />
            <Card label="Average gift (gross)" value={fmtMoney(stats?.avgGift)} />
          </div>
        </div>
      </div>

      {/* Toasts */}
      <div className="fixed top-3 left-1/2 -translate-x-1/2 z-50 space-y-2">
        <AnimatePresence>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              className="px-3 py-2 rounded-lg bg-black text-white text-sm shadow"
            >
              {t.text}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Payout progress / status */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="bg-white rounded-2xl border p-4 mt-4"
      >
        <div className="flex items-center gap-3">
          <FiCreditCard />
          <div>
            <div className="font-medium">Receive gifts & payouts</div>
            <div className="text-xs text-gray-600 mt-0.5">
              Connect Stripe to receive <b>Thanks Gifts</b>. PTfinderNow never
              sees your bank details — Stripe handles verification and payouts.
            </div>
          </div>
        </div>

        {/* Stepper */}
        <div className="mt-3 flex items-center gap-2 text-sm">
          <Step done={!!connect.connected} label="Create account" />
          <FiChevronRight className="text-gray-400" />
          <Step done={!!connect.chargesEnabled} label="Verification" />
          <FiChevronRight className="text-gray-400" />
          <Step done={!!connect.payoutsEnabled} label="Payouts enabled" />
        </div>

        {/* Status box */}
        <div className={`mt-3 text-sm border rounded-xl px-3 py-2 ${payoutBoxCls}`}>
          <div className="font-semibold">{payoutUi.title}</div>
          <div className="text-[13px] mt-1">{payoutUi.text}</div>
          <div className="text-[12px] mt-2 text-gray-700">
            Tip: After finishing in Stripe, return here and press <b>Refresh</b>.
          </div>
        </div>

        {/* Requirements / actions */}
        <div className="mt-3 flex flex-wrap items-start gap-2">
          {connect.loading ? (
            <div className="text-sm">Checking status…</div>
          ) : (
            <>
              {(Array.isArray(connect.requirements) &&
                connect.requirements.length > 0) && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <div className="font-semibold mb-1">
                    Stripe needs this information:
                  </div>
                  <ul className="list-disc ml-4">
                    {connect.requirements.map((r, i) => (
                      <li key={i} className="break-all">
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {needsOnboarding ? (
                <button
                  onClick={setupPayouts}
                  className="px-3 py-1.5 rounded-lg bg-gray-900 text-white"
                >
                  Finish Stripe setup
                </button>
              ) : (
                <button
                  onClick={openStripePayouts}
                  disabled={!connect.canLoginLink}
                  className={`px-3 py-1.5 rounded-lg border ${
                    connect.canLoginLink ? "" : "opacity-60 cursor-not-allowed"
                  }`}
                >
                  Open Stripe payouts
                </button>
              )}

              <button onClick={refreshConnect} className="px-3 py-1.5 rounded-lg border">
                Refresh
              </button>

              <div className="text-xs text-gray-500 mt-1 w-full">
                ⚠️ If payouts are not enabled, gifts will not be sent to your bank
                until you complete Stripe setup.
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Chart */}
      <div ref={chartRef} className="bg-white rounded-2xl p-4 border mt-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 mb-2">Last 30 days (gross)</div>
          <div className="sm:hidden">
            <button
              onClick={() => setSheetOpen(true)}
              className="px-3 py-1.5 rounded-lg border flex items-center gap-2"
            >
              <FiShare2 /> Actions
            </button>
          </div>
        </div>
        {width > 0 ? (
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart
                data={Array.isArray(stats?.series) ? stats.series : []}
                margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
              >
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke="#0ea5e9"
                  fillOpacity={1}
                  fill="url(#g1)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-xs text-gray-400 flex items-center gap-2">
            <FiAlertTriangle /> Resizing…
          </div>
        )}
      </div>

      {/* Recent gifts */}
      <div className="bg-white rounded-2xl border mt-4">
        <div className="p-4 font-medium">Recent gifts</div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-y">
                <Th>Date</Th>
                <Th>Gross</Th>
                <Th>Net (80%)</Th>
                <Th>Fee (20%)</Th>
                <Th>Note</Th>
                <Th>From</Th>
                <Th>Status</Th>
                <Th>Stripe</Th>
              </tr>
            </thead>
            <tbody>
              {(gifts || []).map((g, i) => {
                const gross = money(g?.amount ?? (g?.amountMinor ? g.amountMinor / 100 : 0));
                return (
                  <tr
                    key={g?.id ?? `${g?.createdUtc ?? i}-${g?.stripePaymentIntentId ?? i}`}
                    className="border-b hover:bg-gray-50"
                  >
                    <Td>{fmtDate(g?.createdUtc)}</Td>
                    <Td>{fmtMoney(gross)}</Td>
                    <Td>{fmtMoney(net80(gross))}</Td>
                    <Td>{fmtMoney(fee20(gross))}</Td>
                    <Td className="max-w-[360px] truncate" title={g?.note || ""}>
                      {g?.note || "—"}
                    </Td>
                    <Td>{g?.donorEmail || "—"}</Td>
                    <Td>
                      <Badge status={g?.status} />
                    </Td>
                    <Td>
                      {g?.stripePaymentIntentId ? (
                        <a
                          className="text-blue-600 underline inline-flex items-center gap-1"
                          target="_blank"
                          rel="noreferrer"
                          href={`https://dashboard.stripe.com/payments/${g.stripePaymentIntentId}`}
                        >
                          view <FiExternalLink className="inline" />
                        </a>
                      ) : (
                        "—"
                      )}
                    </Td>
                  </tr>
                );
              })}
              {(!gifts || gifts.length === 0) && (
                <tr>
                  <Td className="text-gray-500" colSpan={8}>
                    No gifts yet.
                  </Td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y">
          {(gifts || []).length === 0 && (
            <div className="p-4 text-sm text-gray-500">No gifts yet.</div>
          )}
          {(gifts || []).map((g, i) => {
            const gross = money(g?.amount ?? (g?.amountMinor ? g.amountMinor / 100 : 0));
            return (
              <div key={g?.id ?? `${g?.createdUtc ?? i}-m`} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">
                      {fmtMoney(gross)}{" "}
                      <span className="text-gray-500">(gross)</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      Net to you: <b>{fmtMoney(net80(gross))}</b> • Fee:{" "}
                      {fmtMoney(fee20(gross))}
                    </div>
                    <div className="text-xs text-gray-500">{fmtDate(g?.createdUtc)}</div>
                  </div>
                  <Badge status={g?.status} />
                </div>
                {g?.note && <div className="mt-2 text-sm line-clamp-3">{g.note}</div>}
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-600">
                  <span className="px-2 py-1 rounded bg-gray-100">
                    {g?.donorEmail || "Anonymous"}
                  </span>
                  {g?.stripePaymentIntentId && (
                    <a
                      className="underline inline-flex items-center gap-1"
                      target="_blank"
                      rel="noreferrer"
                      href={`https://dashboard.stripe.com/payments/${g.stripePaymentIntentId}`}
                    >
                      Stripe <FiExternalLink />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Floating action bar (mobile) */}
      <AnimatePresence>
        {true && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 sm:hidden"
          >
            <div className="flex items-center gap-2 rounded-2xl border bg-white/90 backdrop-blur shadow-lg p-2">
              <button
                onClick={() => copy(giftLink)}
                className="px-3 py-2 rounded-xl border flex items-center gap-2 active:scale-95"
              >
                <FiCopy /> Copy link
              </button>
              {"share" in navigator && (
                <button
                  onClick={() => navigator.share({ title: "Send me a Thanks", url: giftLink })}
                  className="px-3 py-2 rounded-xl border flex items-center gap-2 active:scale-95"
                >
                  <FiShare2 /> Share
                </button>
              )}
              <button
                onClick={exportCsv}
                className="px-3 py-2 rounded-xl border flex items-center gap-2 active:scale-95"
              >
                <FiDownload /> CSV
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action Sheet (mobile) */}
      <AnimatePresence>
        {sheetOpen && (
          <motion.div
            className="fixed inset-0 z-50 sm:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSheetOpen(false)}
          >
            <div className="absolute inset-0 bg-black/30" />
            <motion.div
              initial={{ y: 400 }}
              animate={{ y: 0 }}
              exit={{ y: 400 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mx-auto h-1 w-10 rounded bg-gray-200" />
              <div className="mt-3 space-y-2">
                <button
                  onClick={() => {
                    copy(giftLink);
                    setSheetOpen(false);
                  }}
                  className="w-full px-4 py-3 border rounded-2xl flex items-center gap-2"
                >
                  <FiLink /> Copy gift link
                </button>
                {"share" in navigator && (
                  <button
                    onClick={() => {
                      navigator.share({ title: "Send me a Thanks", url: giftLink });
                      setSheetOpen(false);
                    }}
                    className="w-full px-4 py-3 border rounded-2xl flex items-center gap-2"
                  >
                    <FiShare2 /> Share
                  </button>
                )}
                <button
                  onClick={() => {
                    exportCsv();
                    setSheetOpen(false);
                  }}
                  className="w-full px-4 py-3 border rounded-2xl flex items-center gap-2"
                >
                  <FiDownload /> Export CSV
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// small step component
function Step({ done, label }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`h-5 w-5 rounded-full border flex items-center justify-center ${
          done ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white"
        }`}
      >
        {done ? (
          <FiCheck className="text-[12px]" />
        ) : (
          <span className="text-[11px] text-gray-400">•</span>
        )}
      </div>
      <div className={`text-sm ${done ? "text-emerald-700" : "text-gray-600"}`}>
        {label}
      </div>
    </div>
  );
}
